import { useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { ROUTES } from '@/constants/routes';
import { useNavigationStore } from '@/stores/navigationStore';
import type { Orders } from '@/apis/orders';
import { ShippingType, StatusOrders } from '@/apis/orders';
import { PaymentStatus } from '@/apis/payment';
import { AddNoteDialog } from '@/components/ui/add-note-dialog';
import { ChooseSalespersonDialog } from '@/components/ui/choose-salesperson-dialog';
import { PaymentSystemDialog } from '@/components/ui/payment-system-dialog';
import { useAddOrderNote, useUpdateOrderStatus } from '@/hooks/useAdminOrders';
import { useSalespersonsQuery } from '@/hooks/useSalespersons';
import { useAuthStore } from '@/stores/authStore';
import {
  COUNTRY_FLAG,
  DEFAULT_PAYMENT_STATUS_STYLE,
  FLAG_STYLE,
  getInitialShippingType,
  PAYMENT_METHOD_STYLE,
  PAYMENT_STATUS_STYLE,
  SHIPPING_TYPE_OPTIONS,
  SHIPPING_TYPE_PROGRESS,
} from './order-shared';
import { OrderCardGrid } from './OrderCardGrid';
import { OrderCardList } from './OrderCardList';

interface OrderCardProps {
  order: Orders;
  view: 'grid' | 'list';
  onShippingTypeChange?: (orderId: string, type: ShippingType) => void;
}

export function OrderCard({ order, view, onShippingTypeChange }: OrderCardProps) {
  const navigate = useNavigate();
  const searchParams = useSearch({ from: '/_admin/order-management/' });
  const saveOrderListParams = useNavigationStore((s) => s.saveOrderListParams);
  const isStaff = useAuthStore((s) => s.user?.userRole === 'staff');
  const [shippingType, setShippingType] = useState<ShippingType>(
    getInitialShippingType(order.status)
  );
  const [noteOpen, setNoteOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [salespersonOpen, setSalespersonOpen] = useState(false);

  const addNote = useAddOrderNote(order.id);
  const updateStatus = useUpdateOrderStatus(order.id);

  const anyDialogOpen = noteOpen || paymentOpen || salespersonOpen;

  const handleCardClick = () => {
    if (anyDialogOpen) return;
    saveOrderListParams({
      page:   searchParams.page,
      limit:  searchParams.limit,
      search: searchParams.search,
      status: searchParams.status,
      tab:    searchParams.tab,
      view:   searchParams.view,
    });
    navigate({ to: ROUTES.ORDER_DETAIL(order.id) });
  };

  const handleShippingTypeSelect = (type: ShippingType) => {
    setShippingType(type);
    onShippingTypeChange?.(order.id, type);
    updateStatus.mutate({ status: type as unknown as StatusOrders });
  };

  const handleSaveNote = (text: string) => {
    if (!text.trim()) return;
    addNote.mutate({ text });
  };

  // ── Computed values ──────────────────────────────────────────────────────────

  const customerName  = order.customer?.fullName || 'N/A';
  const customerEmail = order.customer?.email    || 'N/A';
  const customerPhone = order.customer?.phone    || 'N/A';

  const billing  = order.billingAddress;
  const shipping = order.shippingAddress;

  const billingLine = billing
    ? [billing.addressLine1, billing.addressLine2, billing.city, billing.postalCode, billing.state, billing.country]
        .filter(Boolean).join(', ')
    : 'N/A';

  const shippingLine = shipping
    ? [shipping.addressLine1, shipping.addressLine2, shipping.city, shipping.state, shipping.postalCode, shipping.country]
        .filter(Boolean).join(', ')
    : 'N/A';

  const flagEmoji = COUNTRY_FLAG[shipping?.country?.toUpperCase() ?? ''] ?? null;

  const storeName  = order.storefront?.name || 'N/A';
  const orderNum   = order.orderNumber?.replace(/\D/g, '') || 'N/A';
  const shortId    = order.id?.slice(0, 9).toUpperCase() ?? 'N/A';
  const isApproved = order.paymentStatus === PaymentStatus.PAID;

  const rawPaymentMethod   = order.metadata?.latestPaymentMethod ?? order.metadata?.paymentMethod ?? null;
  const paymentMethodStyle = rawPaymentMethod
    ? (PAYMENT_METHOD_STYLE[rawPaymentMethod.toLowerCase()] ?? null)
    : null;
  const paymentMethodLabel = paymentMethodStyle?.label ?? 'N/A';

  const paymentStatusStyle = PAYMENT_STATUS_STYLE[order.paymentStatus] ?? DEFAULT_PAYMENT_STATUS_STYLE;

  const flagInfo = order.fraudRiskLevel
    ? (FLAG_STYLE[order.fraudRiskLevel.toLowerCase()] ?? null)
    : null;

  const progress = SHIPPING_TYPE_PROGRESS[shippingType] ?? {
    value: 40,
    color: '#1565C0',
    textColor: 'text-primary-s500',
    label: shippingType,
  };

  const { data: salespersons } = useSalespersonsQuery();
  const sp = order.assignedSalesperson
    ?? salespersons?.find((s) => s.id === order.assignedSalespersonId);
  const salespersonName = sp ? `${sp.firstName} ${sp.lastName}`.trim() : null;

  // ── Shared UI props ──────────────────────────────────────────────────────────

  const uiProps = {
    order,
    orderNum,
    shortId,
    storeName,
    customerName,
    customerEmail,
    customerPhone,
    billingLine,
    shippingLine,
    flagEmoji,
    salespersonName,
    paymentMethodStyle,
    paymentMethodLabel,
    paymentStatusStyle,
    flagInfo,
    progress,
    shippingType,
    isApproved,
    isStaff,
    noteOpen,
    paymentOpen,
    salespersonOpen,
    isSavingNote: addNote.isPending,
    onCardClick: handleCardClick,
    onShippingTypeSelect: handleShippingTypeSelect,
    onNoteOpenChange: setNoteOpen,
    onPaymentOpenChange: setPaymentOpen,
    onSalespersonOpenChange: setSalespersonOpen,
    onSaveNote: handleSaveNote,
  };

  return (
    <>
      {view === 'grid' ? (
        <OrderCardGrid {...uiProps} />
      ) : (
        <OrderCardList {...uiProps} />
      )}

      <AddNoteDialog
        open={noteOpen}
        onOpenChange={setNoteOpen}
        notes={order.metadata?.notes ?? []}
        isSaving={addNote.isPending}
        closeOnSave={false}
        orderId={order.id}
        salespersonFiles={(order.metadata?.salespersonFiles as Array<{ id: string; name: string; url: string; uploadedAt: string }>) ?? []}
        onSave={handleSaveNote}
      />
      <PaymentSystemDialog
        open={paymentOpen}
        onOpenChange={setPaymentOpen}
        order={order}
      />
      <ChooseSalespersonDialog
        open={salespersonOpen}
        onOpenChange={setSalespersonOpen}
        orderId={order.id}
        currentSalespersonId={order.assignedSalespersonId}
      />
    </>
  );
}
